import React, {
  FC,
  useEffect,
  useState,
  useMemo,
  MutableRefObject
} from 'react';
import {
  ModalPosition,
  ModalRounding
} from 'src/shared-components/Modal/Modal';
import cn from 'classnames';
import {
  generateCatalogPageLink,
  generateFirstLevelCategoryPageLink,
  generateSecondLevelCategoryPageLink,
  generateThirdLevelCategoryPageLink
} from 'src/utils/links';
import { FirstCategoryLevel } from 'src/api/types';
import NextImage from 'src/shared-components/NextImage/NextImage';
import dynamic from 'next/dynamic';
import useModalStore from 'src/hooks/useModalStore';
import useMedia from 'src/hooks/useMedia';
import { FormattedMessage } from 'react-intl';
import ButtonBase, {
  ButtonColor,
  ButtonSize
} from 'src/shared-components/Button';
import { useRouter } from 'next/router';
import useSSRMultyLevelCategoriesStore from 'src/hooks/useSSRMultyLevelCategoriesStore';
import NavigationList from './MobileNavigationList';
import NavigationLink from '../NavigationLink/NavigationLink';
import trns from './trns';

const Modal = dynamic(() => import('src/shared-components/Modal/Modal'), {
  ssr: false
});

const arrow = 'shared/arrow-tab.svg';
const allCategoriesIcon = 'shared/all-categories.svg';

const SelectedCategoryHeader = ({
  categoryName,
  onClick
}: {
  categoryName: string;
  onClick: () => void;
}) => (
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
  <div
    className="bg-wildSand text-astronautBlue fs-body-lg-bold flex items-center h-14"
    onClick={onClick}
  >
    <div className="flex items-center justify-center mx-5 origin-center rotate-180">
      <NextImage
        alt={categoryName}
        title={categoryName}
        src={arrow}
        layout="fixed"
        quality={100}
        width={11}
        height={16}
        priority
      />
    </div>
    <p>{categoryName}</p>
  </div>
);

interface Props {
  parentRef: MutableRefObject<HTMLElement>;
  initialCategory?: FirstCategoryLevel;
  selectedSubCategoryId?: string;
  onCloseMenu: () => void;
  onGoToCategory: () => void;
  style?: { [key: string]: string };
}

const MobileCategoriesDropdown: FC<Props> = ({
  parentRef,
  initialCategory,
  selectedSubCategoryId,
  onCloseMenu,
  onGoToCategory,
  style
}) => {
  const router = useRouter();
  const { xxs } = useMedia();
  const { multyLevelCategoriesList } = useSSRMultyLevelCategoriesStore();

  const [selectedCategory, setSelectedCategory] =
    useState<FirstCategoryLevel | null>(null);

  const { isModalVisible } = useModalStore();
  const modalVisible = isModalVisible('mobile-categories-dropdown-modal');

  useEffect(() => {
    // Handle force category set
    if (initialCategory && initialCategory.translitName) {
      setSelectedCategory(initialCategory);
    }
  }, [
    // TODO: array as useEffect dependency should be replaced with primitive. Or deep use effect utilized
    multyLevelCategoriesList,
    initialCategory
  ]);

  useEffect(() => {
    if (!modalVisible) {
      setSelectedCategory(
        initialCategory && initialCategory.translitName ? initialCategory : null
      );
    }
  }, [modalVisible]);

  const subCategoriesList = useMemo(
    () => (
      <div
        className={cn(
          'py-5 px-2.5 grid grid-cols-1 gap-5 z-10 bg-white rounded-b-xl auto-rows-max h-full w-full'
        )}
      >
        {multyLevelCategoriesList?.map((category) => {
          // Render all items to make them visible for indexing
          // Hide all items except selected via CSS to show only selected item to a member
          return category?.subCategories
            ?.slice(0, 6)
            .map((categorySecondLevel) => {
              const isVisible =
                selectedCategory?.translitName === category.translitName;
              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                <div
                  key={categorySecondLevel.translitName}
                  className={cn(
                    'min-h-[36px]  border-white cursor-pointer border-b text-black',
                    !isVisible && 'hidden'
                  )}
                >
                  <div>
                    <div className={cn('flex items-start cursor-pointer ')}>
                      <NavigationLink
                        key={categorySecondLevel.name}
                        onClick={() => onGoToCategory()}
                        href={generateSecondLevelCategoryPageLink({
                          subCategoryId: categorySecondLevel?.translitName,
                          category
                        })}
                        passClassName="fs-body-bold"
                      >
                        {categorySecondLevel.name}
                      </NavigationLink>
                    </div>
                    {categorySecondLevel?.subCategories?.length && (
                      <div className="w-full h-[1px] bg-alto my-2 "></div>
                    )}
                    <div className="mb-3">
                      {categorySecondLevel?.subCategories
                        ?.slice(0, 4)
                        .map((categoryThirdLevel) => (
                          <div
                            key={categoryThirdLevel.name}
                            className="flex flex-col  py-2  "
                          >
                            <NavigationLink
                              key={categoryThirdLevel.name}
                              onClick={() => onGoToCategory()}
                              href={generateThirdLevelCategoryPageLink({
                                category,
                                secondCategoryId:
                                  categorySecondLevel?.translitName,
                                thirdCategoryId:
                                  categoryThirdLevel?.translitName
                              })}
                              passClassName="fs-body-lg-reg leading-6 hover:underline hover:text-bahamaBlue"
                            >
                              {categoryThirdLevel.name}
                            </NavigationLink>
                          </div>
                        ))}
                    </div>
                  </div>
                  {categorySecondLevel?.subCategories?.length > 0 && (
                    <NavigationLink
                      href={generateSecondLevelCategoryPageLink({
                        subCategoryId: categorySecondLevel?.translitName,
                        category
                      })}
                      onClick={() => onGoToCategory()}
                      passClassName="fs-body-lg-reg w-full leading-6 underline text-astronautBlue"
                    >
                      <FormattedMessage {...trns.viewAllCategories} />
                    </NavigationLink>
                  )}
                </div>
              );
            });
        })}
      </div>
    ),
    [selectedSubCategoryId, selectedCategory]
  );

  if (!modalVisible) {
    return null;
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <Modal
      style={style}
      id="mobile-categories-dropdown-modal"
      position={ModalPosition.RelativeBottom}
      roundingMode={ModalRounding.Bottom}
      withCloseIcon={false}
      onClose={onCloseMenu}
      triggerElementRef={parentRef}
      customHeader={
        selectedCategory ? (
          <SelectedCategoryHeader
            categoryName={selectedCategory.name}
            onClick={() => setSelectedCategory(null)}
          />
        ) : null
      }
    >
      <div
        className={cn(
          'compendium-constraints w-full fs-caption-reg relative h-full',
          {
            flex: !xxs
          }
        )}
      >
        {!selectedCategory && (
          <div className={cn('flex flex-col bg-opacity-100 z-20  ')}>
            <NavigationList
              listClassName={cn('m:pt-5 w-full')}
              items={multyLevelCategoriesList}
              selectedItem={selectedCategory}
              setSelectedItem={
                setSelectedCategory as (item: FirstCategoryLevel) => void
              }
            />
            <NavigationLink
              key={selectedCategory?.translitName}
              href={generateCatalogPageLink()}
              onClick={onGoToCategory}
              passClassName="w-full flex items-center justify-start py-4 text-doveGray fs-body-bold "
            >
              <div className="flex items-center justify-center w-19 ">
                <NextImage
                  alt="allCategoriesIcon"
                  src={allCategoriesIcon}
                  layout="fixed"
                  height={24}
                  width={24}
                />
              </div>
              <FormattedMessage {...trns.viewCatalog} />
            </NavigationLink>
          </div>
        )}
        {selectedCategory && subCategoriesList}
        {selectedCategory?.subCategories?.length > 6 && (
          <div className="mt-6">
            <ButtonBase
              onClick={() => {
                router.push(
                  generateFirstLevelCategoryPageLink({
                    category: selectedCategory
                  })
                );
                onGoToCategory();
              }}
              color={ButtonColor.blue}
              size={ButtonSize.lg}
              className="flex text-white rounded-md "
              innerClassName="w-full "
              label={
                <FormattedMessage
                  {...trns.viewCategory}
                  values={{
                    selectedCategory: selectedCategory?.name
                  }}
                />
              }
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MobileCategoriesDropdown;
